import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";

import { AuthProvider } from "./services/AuthContext";
import { ToastProvider } from "./components/common/ToastProvider";
import ErrorBoundary from "./components/common/ErrorBoundary";
import LoadingSpinner from "./components/common/LoadingSpinner";
import { ThemeProvider } from "./theme/ThemeContext";
import { BrandingProvider } from "./theme/BrandingContext";
import { ModuleProvider, useModules } from "./theme/ModuleContext";

import AdminAuth from "./auth/AdminAuth";
import PublicOnly from "./auth/PublicOnly";
import RequirePermission from "./components/common/RequirePermission";
import AdminLayout from "./layout/AdminLayout";
import Login from "./pages/Login";
// Public, unauthenticated.  Reached only via the email link sent from
// /auth/forgot-password.  Lazy-loaded so the login bundle stays small.
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
// Where the identity provider drops the browser after a federated sign-in.
const SsoCallback = lazy(() => import("./pages/SsoCallback"));
// Customer star ratings collected by email.
const Feedback = lazy(() => import("./pages/Feedback"));

/**
 * App router — Round 2 refactor:
 *  - Layout routes: wrap all /admin/* under a single <AdminAuth><AdminLayout />
 *    element so we stop repeating the guard in every <Route>. Reduces 150+
 *    lines of boilerplate to one block.
 *  - Code splitting: every page loads on demand via React.lazy, so the
 *    initial bundle only ships Login + Dashboard shell.
 *  - ErrorBoundary at the app root + per-route so a crash on one page
 *    doesn't blank out the entire admin panel.
 *  - Added 404 (NotFound) + 403 (Forbidden) routes.
 *  - Role-gated routes: Monitoring pages require elevated roles via the
 *    `roles` prop on AdminAuth.
 */

// ─── Lazy page imports (code-split per route) ────────────────────
const Dashboard          = lazy(() => import("./pages/Dashboard"));
const ActivityPage       = lazy(() => import("./pages/ActivityPage"));
const Employees          = lazy(() => import("./pages/Employees"));
const Departments        = lazy(() => import("./pages/Departments"));
const Shifts             = lazy(() => import("./pages/Shifts"));
const MobileReleases     = lazy(() => import("./pages/MobileReleases"));
const Attendance         = lazy(() => import("./pages/Attendance"));
const Leaves             = lazy(() => import("./pages/Leaves"));
const LeaveBalances      = lazy(() => import("./pages/LeaveBalances"));
const Payroll            = lazy(() => import("./pages/Payroll"));
const EditEmployee       = lazy(() => import("./pages/EditEmployee"));
const EmployeeView       = lazy(() => import("./pages/EmployeeView"));
const AttendanceSettings = lazy(() => import("./pages/settings/AttendanceSettings"));
const LeavePolicySettings= lazy(() => import("./pages/settings/LeavePolicySettings"));
const MonitoringSettings = lazy(() => import("./pages/settings/MonitoringSettings"));
const NotificationSettings = lazy(() => import("./pages/settings/NotificationSettings"));
const CompanyBranding    = lazy(() => import("./pages/settings/CompanyBranding"));
const SecuritySettings   = lazy(() => import("./pages/settings/SecuritySettings"));
const MaintenanceSettings = lazy(() => import("./pages/settings/MaintenanceSettings"));
// Public Maintenance screen — shown on 503-from-server.  Reached either
// directly via /maintenance or routed automatically by the axios interceptor.
const Maintenance        = lazy(() => import("./pages/Maintenance"));
const HolidaysSettings   = lazy(() => import("./pages/settings/HolidaysSettings"));
const IntegrationsSettings = lazy(() => import("./pages/settings/IntegrationsSettings"));
const PermissionSettings = lazy(() => import("./pages/settings/PermissionSettings"));
const EmailTemplatesSettings = lazy(() => import("./pages/settings/EmailTemplatesSettings"));
const TicketMailboxSettings = lazy(() => import("./pages/settings/TicketMailboxSettings"));
const ModuleSettings     = lazy(() => import("./pages/settings/ModuleSettings"));
const SystemAdmin        = lazy(() => import("./pages/System"));
const Assets             = lazy(() => import("./pages/Assets"));
const Documents          = lazy(() => import("./pages/Documents"));
const BackupModule       = lazy(() => import("./pages/BackupModule"));
const AdvancedReports    = lazy(() => import("./pages/AdvancedReports"));
const Projects           = lazy(() => import("./pages/Projects"));
const Tickets            = lazy(() => import("./pages/Tickets"));
const MyProfile          = lazy(() => import("./pages/MyProfile"));
const TravelLog          = lazy(() => import("./pages/TravelLog"));
const LiveTravel         = lazy(() => import("./pages/LiveTravel"));
const Clients            = lazy(() => import("./pages/Clients"));
const Reimbursements     = lazy(() => import("./pages/Reimbursements"));
const AnnouncementPage   = lazy(() => import("./pages/AnnouncementPage"));
const LiveScreen         = lazy(() => import("./pages/monitor/Livescreen"));
const UserScreenshots    = lazy(() => import("./pages/monitor/UserScreenshots"));
const MonitorDashboard   = lazy(() => import("./pages/monitor/MonitorUser"));
const MonitoringDashboard = lazy(() => import("./pages/monitor/MonitoringDashboard"));
const Screenshots        = lazy(() => import("./pages/monitor/Screenshots"));
const LiveView           = lazy(() => import("./pages/monitor/LiveView"));
const MonitorPage        = lazy(() => import("./pages/monitor/Monitor"));
const Forbidden          = lazy(() => import("./pages/Forbidden"));
const ModuleDisabled     = lazy(() => import("./pages/ModuleDisabled"));
const NotFound           = lazy(() => import("./pages/NotFound"));
const StatsDashboard     = lazy(() => import("./pages/monitor/StatsDashboard"));
const Reports            = lazy(() => import("./pages/monitor/Reports"));
const Activities         = lazy(() => import("./pages/monitor/Activities"));
const Keystrokes         = lazy(() => import("./pages/monitor/Keystrokes"));
const Productivity       = lazy(() => import("./pages/monitor/Productivity"));
const ShutdownLogs       = lazy(() => import("./pages/monitor/ShutdownLogs"));
const MyMonitoringHours  = lazy(() => import("./pages/monitor/MyMonitoringHours"));
const Certificates       = lazy(() => import("./pages/Certificates"));
const VerifyCertificate  = lazy(() => import("./pages/VerifyCertificate"));
const Cctv               = lazy(() => import("./pages/Cctv"));
const ApiIntegrations    = lazy(() => import("./pages/settings/ApiIntegrations"));

// Roles allowed to see the monitoring section. Extend as needed.
const MONITOR_ROLES = ["ADMIN", "MANAGER", "TEAM_LEADER", "HR_ADMIN", "PROJECT_HEAD"];

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrandingProvider>
        <ModuleProvider>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
            <Suspense fallback={<LoadingSpinner label="Loading page..." />}>
              <Routes>
                {/* PUBLIC */}
                <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
                {/* Landing spot for the SSO redirect. NOT wrapped in
                    PublicOnly: it must run its ticket exchange even though a
                    stale token may still be sitting in storage. */}
                <Route path="/sso/callback" element={<SsoCallback />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* Given to Google Play as the app's privacy policy URL, so it
                    MUST stay public and MUST NOT be wrapped in PublicOnly —
                    that would redirect a signed-in admin away, and Play's
                    reviewers (who have no session) must reach it too. A
                    redirect here reads to Google as "no privacy policy". */}
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/verify/:publicId" element={<VerifyCertificate />} />
                <Route path="/admin/forbidden" element={<Forbidden />} />

                {/* AUTHENTICATED /admin/* — shared layout, single guard */}
                <Route
                  element={
                    <AdminAuth>
                      <AdminLayoutRoute />
                    </AdminAuth>
                  }
                >
                  <Route path="/admin" element={<Dashboard />} />
                  <Route path="/admin/activity" element={<ActivityPage />} />
                  <Route path="/admin/employees" element={<Employees />} />
                  <Route path="/admin/departments" element={<Departments />} />
                  <Route path="/admin/employees/:id" element={<EmployeeView />} />
                  <Route path="/admin/employees/edit/:id" element={<EditEmployee />} />
                  <Route path="/admin/attendance" element={<Attendance />} />
                  {/* Both perms accepted: shifts.view is read-only, shifts.manage
                      implies it. Listing only one is how view_own users got
                      locked out of the projects page.
                      NOTE the prop is `permissions` (plural) — `permission`
                      takes a single key and would test the array itself. */}
                  <Route path="/admin/shifts" element={<RequirePermission permissions={["shifts.view", "shifts.manage"]}><Shifts /></RequirePermission>} />
                  <Route path="/admin/mobile-releases" element={<RequirePermission permission="mobile.release.manage"><MobileReleases /></RequirePermission>} />
                  <Route path="/admin/leaves" element={<ModuleRoute moduleKey="leaves" permission={["leaves.view", "leaves.view_all"]}><Leaves /></ModuleRoute>} />
                  <Route path="/admin/leave-balances" element={<ModuleRoute moduleKey="leaves" permission="leaves.balance.write"><LeaveBalances /></ModuleRoute>} />
                  <Route path="/admin/payroll" element={<RequirePermission permission="payroll.view"><Payroll /></RequirePermission>} />
                  <Route path="/admin/travel-logs" element={<RequirePermission permission="travel.view"><TravelLog /></RequirePermission>} />
                  <Route path="/admin/live-travel" element={<RequirePermission permission="travel.view"><LiveTravel /></RequirePermission>} />
                  <Route path="/admin/clients" element={<RequirePermission permission="clients.view"><Clients /></RequirePermission>} />
                  <Route path="/admin/reimbursements" element={<ModuleRoute moduleKey="reimbursements" permission={["reimbursements.view_own", "reimbursements.view", "reimbursements.view_all"]}><Reimbursements /></ModuleRoute>} />
                  <Route path="/admin/announcements" element={<AnnouncementPage />} />
                  <Route path="/admin/feedback" element={<RequirePermission permission="feedback.view"><Feedback /></RequirePermission>} />
                  <Route path="/admin/certificates" element={<Certificates />} />
                  <Route path="/cctv" element={<RequirePermission permission="cctv.view"><Cctv /></RequirePermission>} />
                  <Route path="/admin/settings/attendance" element={<ModuleRoute moduleKey="attendance"><AttendanceSettings /></ModuleRoute>} />
                  <Route path="/admin/settings/leave-policy" element={<ModuleRoute moduleKey="leaves"><LeavePolicySettings /></ModuleRoute>} />
                  <Route path="/admin/settings/monitoring" element={<ModuleRoute moduleKey="tracker"><MonitoringSettings /></ModuleRoute>} />
                  <Route path="/admin/settings/notifications" element={<NotificationSettings />} />
                  <Route path="/admin/settings/company" element={<CompanyBranding />} />
                  <Route path="/admin/settings/security" element={<SecuritySettings />} />
                  <Route path="/admin/settings/maintenance" element={<MaintenanceSettings />} />
                  <Route path="/admin/settings/holidays" element={<ModuleRoute moduleKey="leaves"><HolidaysSettings /></ModuleRoute>} />
                  <Route path="/admin/settings/integrations" element={<IntegrationsSettings />} />
                  <Route path="/admin/settings/api-integrations" element={<RequirePermission permission="settings.write"><ApiIntegrations /></RequirePermission>} />
                  <Route path="/admin/settings/permissions" element={<PermissionSettings />} />
                  <Route path="/admin/settings/email-templates" element={<EmailTemplatesSettings />} />
                  <Route path="/admin/settings/ticket-mailboxes" element={<ModuleRoute moduleKey="tickets"><TicketMailboxSettings /></ModuleRoute>} />
                  <Route path="/admin/settings/modules" element={<ModuleSettings />} />
                  <Route path="/admin/system" element={<ModuleRoute moduleKey="system"><SystemAdmin /></ModuleRoute>} />
                  <Route path="/admin/assets" element={<ModuleRoute moduleKey="assets" permission="assets.view"><Assets /></ModuleRoute>} />
                  <Route path="/admin/documents" element={<ModuleRoute moduleKey="documents" permission="documents.view"><Documents /></ModuleRoute>} />
                  <Route path="/admin/backups" element={<ModuleRoute moduleKey="backup" permission="backup.view"><BackupModule /></ModuleRoute>} />
                  <Route path="/admin/reports" element={<ModuleRoute moduleKey="reports" permission={["reports.view", "reports.view_all"]}><AdvancedReports /></ModuleRoute>} />
                  {/* All three view scopes must be listed. The sidebar already
                      included view_own, so omitting it here showed the link and
                      then refused the page — which reads as a broken app rather
                      than a permission. The backend narrows what's returned. */}
                  <Route path="/admin/projects" element={<ModuleRoute moduleKey="projects" permission={["projects.view", "projects.view_all", "projects.view_own"]}><Projects /></ModuleRoute>} />
                  <Route path="/admin/tickets" element={<ModuleRoute moduleKey="tickets" permission={["tickets.view_own", "tickets.view_department", "tickets.view_all"]}><Tickets /></ModuleRoute>} />
                  <Route path="/admin/my-profile" element={<MyProfile />} />
                  <Route path="/admin/my-monitoring-hours" element={<ModuleRoute moduleKey="tracker"><MyMonitoringHours /></ModuleRoute>} />
                </Route>

                {/* ROLE-GATED /admin/monitoring/* */}
                <Route
                  element={
                    <AdminAuth roles={MONITOR_ROLES}>
                      <AdminLayoutRoute />
                    </AdminAuth>
                  }
                >
                  <Route path="/admin/statedashboard" element={<StatsDashboard/>}/>
                  <Route path="/admin/monitor" element={<MonitorPage />} />
                  <Route path="/admin/monitoring/dashboard" element={<MonitoringDashboard />} />
                  <Route path="/admin/monitoring/users" element={<MonitorDashboard />} />
                  <Route path="/admin/monitoring/live-screens" element={<LiveScreen />} />
                  <Route path="/admin/monitoring/screenshots" element={<Screenshots />} />
                  <Route path="/admin/monitoring/reports" element={<Reports />} />
                  <Route path="/admin/monitoring/activities" element={<Activities />} />
                  <Route path="/admin/monitoring/keystrokes" element={<Keystrokes />} />
                  <Route path="/admin/monitoring/productivity" element={<Productivity />} />
                  <Route path="/admin/monitoring/shutdown-logs" element={<ShutdownLogs />} />
                  <Route path="/admin/screenshots/:username" element={<UserScreenshots />} />
                  <Route path="/live/:username" element={<LiveView />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
        </ModuleProvider>
        </BrandingProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * Gate a route on BOTH the module being enabled and the user holding at least
 * one of the required permissions.
 *
 * Without the permission check a user could open a module they have no rights
 * to, the page would fire its API calls, and the backend's 403 left them
 * staring at a broken screen (previously it even logged them out). Now they get
 * the standard "you don't have access to this page" panel from
 * <RequirePermission> instead.
 *
 * `permission` accepts a single key or an array; omit it to gate on the module
 * flag alone.
 */
function ModuleRoute({ moduleKey, permission, children }) {
  const { isModuleEnabled } = useModules();
  if (!isModuleEnabled(moduleKey)) return <ModuleDisabled />;
  if (!permission) return children;
  const keys = Array.isArray(permission) ? permission : [permission];
  return <RequirePermission permissions={keys}>{children}</RequirePermission>;
}

/**
 * AdminLayoutRoute — thin wrapper that mounts the admin chrome (sidebar +
 * main area) and renders the matched child route via <Outlet/>. Also wraps
 * the page in an ErrorBoundary so a crash in one page doesn't blank the
 * entire admin shell.
 */
function AdminLayoutRoute() {
  return (
    <AdminLayout>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </AdminLayout>
  );
}
